import React, { useState, useEffect, useRef } from "react";

import Tooltip from "@atlaskit/tooltip";
import EditorCloseIcon from "@atlaskit/icon/glyph/editor/close";
import MailIcon from "@atlaskit/icon/glyph/editor/mention";
import LinkIcon from "@atlaskit/icon/glyph/editor/link";

import PageIcon from "./PageIcon";
import PagesSidebarTransition from "./PagesSidebarTransition";
import { Select } from "@caelor/ui";
import { MACRO_EDIT_EVENTS } from "@Shared/events/macro-edit-events";
import { useClickOutside } from "@Shared/Hooks/useClickOutside";
import { Container, CloseButton, TextContainer, Input } from "./styles";
import { formatLink, findOption } from "./LinkService";
import { SOURCES_OPTIONS, LinkTypes } from "./LinkTypes";
import { useTranslation } from "react-i18next";

const LinkPicker = ({ value, onChange }) => {
  const { t } = useTranslation();

  const { link, source, title } = value || {};
  const hasLink = typeof link === "string" && !!link.length;

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(findOption(source, t));

  const linkPickerRef = useRef(null);

  useClickOutside(linkPickerRef, () => formatLink(link, source, onChange));

  const sourceOptions = SOURCES_OPTIONS(t);

  useEffect(() => {
    window.AP.events.on(MACRO_EDIT_EVENTS.OPEN_LINK_PICKER, () =>
      setIsOpen(true)
    );
  }, []);

  useEffect(() => {
    if (value?.source !== selected.value) {
      const option = findOption(value?.source, t);
      setSelected(option);
    }
  }, [value, t]);

  return (
    <div>
      <Select
        value={selected}
        onChange={(option) => {
          setSelected(option);
          onChange({ source: option.value, link: "" });
        }}
        options={sourceOptions}
      />

      <Container ref={linkPickerRef}>
        {selected.value === LinkTypes.PAGE && (
          <Tooltip content={title}>
            <TextContainer onClick={() => setIsOpen(true)} isLink>
              <Input
                type="text"
                placeholder={t("select-confluence-page")}
                style={{ padding: hasLink ? "4px 8px 4px 30px" : "4px 8px" }}
                value={source === LinkTypes.PAGE ? title : ""}
              />

              {hasLink && (
                <span style={{ margin: "4px" }}>
                  <PageIcon />
                </span>
              )}
            </TextContainer>
          </Tooltip>
        )}

        {selected.value === LinkTypes.LINK && (
          <TextContainer>
            <Input
              type="text"
              value={source === LinkTypes.LINK ? link : ""}
              placeholder="google.com"
              onKeyUp={(e) => {
                if (link && e.key === "Enter") {
                  formatLink(link, source, onChange);
                }
              }}
              onChange={(e) =>
                onChange({
                  link: e.target.value,
                  source: LinkTypes.LINK,
                })
              }
            />
            <LinkIcon size="medium" />
          </TextContainer>
        )}

        {selected.value === LinkTypes.EMAIL && (
          <TextContainer>
            <Input
              type="text"
              value={source === LinkTypes.EMAIL ? link : ""}
              placeholder="info@caelor.com"
              onKeyUp={(e) => {
                if (link && e.key === "Enter") {
                  formatLink(link, source, onChange);
                }
              }}
              onChange={(e) =>
                onChange({
                  link: e.target.value,
                  source: LinkTypes.EMAIL,
                })
              }
            />
            <MailIcon size="medium" />
          </TextContainer>
        )}

        {hasLink && (
          <Tooltip content={t("remove-link")}>
            <CloseButton onClick={() => onChange({})}>
              <EditorCloseIcon />
            </CloseButton>
          </Tooltip>
        )}
      </Container>

      <PagesSidebarTransition
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        value={link}
        onChange={(value) => {
          onChange(value);
          setIsOpen(false);
        }}
      />
    </div>
  );
};

export default LinkPicker;
